/** Category codes sent to the backend; labels are translated, codes never are. */
export const SUPPORT_CATEGORY_CODES = [
  'TECHNICAL_SUPPORT',
  'CRITICAL_ISSUE',
  'SUGGESTION',
  'GENERAL_FEEDBACK',
] as const;

export type SupportCategoryCode = (typeof SUPPORT_CATEGORY_CODES)[number];

/** A file attached in the modal. `id` is local and stable across re-renders. */
export interface AttachedFile {
  id: string;
  file: File;
}

export interface SupportRequest {
  category: SupportCategoryCode;
  /** Trimmed description, 1..5000 code points. */
  description: string;
  /** Trimmed time-period text (<= 200 code points); null when left empty. */
  timePeriod: string | null;
  /** Browser IANA time zone, so the team can match the report to system logs. */
  timeZone: string;
  files: File[];
}

export type SupportErrorKind =
  /** HTTP 429. */
  | 'rate_limit'
  /** HTTP 5xx. */
  | 'server'
  /** The request never got an answer. */
  | 'network'
  /** HTTP 422: the server refused some files (content does not match the type). */
  | 'files_rejected';

export class SupportServiceError extends Error {
  readonly kind: SupportErrorKind;
  readonly status: number | null;
  /** Names of the files the server refused (kind === 'files_rejected'). */
  readonly rejectedFileNames: string[];

  constructor(
    kind: SupportErrorKind,
    status: number | null = null,
    rejectedFileNames: string[] = [],
  ) {
    super(`support request failed: ${kind}`);
    this.name = 'SupportServiceError';
    this.kind = kind;
    this.status = status;
    this.rejectedFileNames = rejectedFileNames;
  }
}

export type AttachmentIssue =
  /** Extension is not PDF, PNG, JPG or CSV. */
  | { code: 'type' }
  /** Extension is allowed, but the MIME type says it is something else. */
  | { code: 'mismatch' }
  /** The file alone is over the total limit. */
  | { code: 'too_large' }
  /** The file pushes the total over the limit; `left` bytes remained. */
  | { code: 'total_exceeded'; left: number };
